"use client"

import { useCallback, useEffect, useId, useRef, useState } from "react"
import { createPortal } from "react-dom"
import NextImage, { type ImageProps } from "next/image"
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock"
import { X } from "lucide-react"

type Props = ImageProps & { basePath: string }

function resolveSrc(src: ImageProps["src"], basePath: string): string | ImageProps["src"] {
	if (typeof src === "string") {
		return `${basePath}${src}`
	}
	return src
}

export default function MdxImageLightbox({ basePath, src, alt, className, ...rest }: Props) {
	const resolved = resolveSrc(src, basePath)
	const dialogId = useId()
	const [open, setOpen] = useState(false)
	const [mounted, setMounted] = useState(false)
	const overlayRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		setMounted(true)
	}, [])

	useEffect(() => {
		if (!open) {
			return
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setOpen(false)
			}
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [open])

	useEffect(() => {
		const el = overlayRef.current
		if (!open || !el) {
			return
		}
		disableBodyScroll(el, { reserveScrollBarGap: true })
		return () => enableBodyScroll(el)
	}, [open])

	const close = useCallback(() => setOpen(false), [])

	const zoomSrc = typeof resolved === "string" ? resolved : undefined

	return (
		<>
			<button
				type="button"
				className={`group relative inline-block max-w-full border-0 bg-transparent p-0 text-left ${className ?? ""}`}
				onClick={() => typeof resolved === "string" && setOpen(true)}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-controls={dialogId}
				title="Kliknij, aby powiększyć"
			>
				<NextImage
					src={resolved}
					alt={alt ?? ""}
					className="cursor-zoom-in transition-opacity group-hover:opacity-95"
					{...rest}
				/>
			</button>
			{mounted &&
				open &&
				zoomSrc &&
				createPortal(
					<div
						ref={overlayRef}
						id={dialogId}
						role="dialog"
						aria-modal="true"
						aria-label={typeof alt === "string" && alt ? alt : "Powiększony obraz"}
						/* Szablon: .sticky-bar.stick ma z-index ~999999 — musimy być wyżej */
						className="fixed inset-0 !z-[2147483647] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
						style={{ zIndex: 2147483647 }}
						onClick={close}
					>
						<button
							type="button"
							className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white shadow-lg ring-1 ring-white/20 transition-colors hover:bg-white/20"
							onClick={close}
							aria-label="Zamknij"
						>
							<X className="h-6 w-6" aria-hidden />
						</button>
						{/* Lightbox: pełna rozdzielczość bez optymalizacji Next — ten sam URL co w treści */}
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img
							src={zoomSrc}
							alt={typeof alt === "string" ? alt : ""}
							className="max-h-[min(90vh,100%)] max-w-[min(90vw,100%)] object-contain shadow-2xl"
							onClick={(e) => e.stopPropagation()}
						/>
					</div>,
					document.body
				)}
		</>
	)
}
